<template>
  <div class="space-y-4 sm:space-y-6">
    <!-- Page Header -->
    <div>
      <h1 class="text-2xl font-semibold text-gray-100">Integrations</h1>
      <p class="mt-1 text-sm text-gray-400">Connect your store and configure search settings</p>
    </div>

    <!-- Shopify Integration -->
    <div class="bg-dark-secondary shadow rounded-lg p-4 sm:p-6">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <h3 class="text-lg sm:text-xl font-medium text-gray-100">Shopify Integration</h3>
          <p class="mt-1 text-sm text-gray-400">
            Connect your Shopify store to enable AI-powered search
          </p>
          <div class="mt-2 flex flex-wrap items-center text-xs text-gray-400">
            <span class="font-mono text-gray-300">read_orders</span>
            <span class="mx-2">•</span>
            <span class="font-mono text-gray-300">read_inventory</span>
            <span class="mx-2">•</span>
            <span class="font-mono text-gray-300">read_products</span>
          </div>
          <div class="mt-4 inline-flex items-center px-3 py-1.5 rounded-lg border" :class="isConnected ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-yellow-500/10 border-yellow-500/20'">
            <div class="flex items-center gap-2">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" :class="isConnected ? 'bg-emerald-500' : 'bg-yellow-500'"></span>
                <span class="relative inline-flex rounded-full h-2 w-2" :class="isConnected ? 'bg-emerald-500' : 'bg-yellow-500'"></span>
              </span>
              <span class="text-sm font-medium" :class="isConnected ? 'text-emerald-500' : 'text-yellow-500'">
                {{ isConnected ? 'Connected to Shopify' : 'Not Connected' }}
              </span>
            </div>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="space-y-4">
            <!-- Shopify Credentials -->
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Store Name</label>
                <div class="flex items-center">
                  <input
                    v-model="shopifyCredentials.storeName"
                    type="text"
                    class="flex-1 bg-dark-secondary border border-gray-700 rounded-l-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="my-store"
                  />
                  <span class="bg-dark-secondary border border-l-0 border-gray-700 rounded-r-md px-3 py-2 text-gray-400">.myshopify.com</span>
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">API Key (Legacy)</label>
                  <input
                    v-model="shopifyCredentials.apiKey"
                    type="text"
                    class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your Shopify API Key"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Secret Key (Legacy)</label>
                  <input
                    v-model="shopifyCredentials.secretKey"
                    type="password"
                    class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your Shopify Secret Key"
                  />
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div class="flex items-center gap-1 mb-2">
                    <label class="text-sm font-medium text-gray-300">Client ID</label>
                    <div class="relative group">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none w-64 text-center">
                        Request these credentials from your Search+ account manager
                        <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <input
                    v-model="shopifyCredentials.clientId"
                    type="text"
                    class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your Client ID"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-1 mb-2">
                    <label class="text-sm font-medium text-gray-300">Client Secret</label>
                    <div class="relative group">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none w-64 text-center">
                        Request these credentials from your Search+ account manager
                        <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <input
                    v-model="shopifyCredentials.clientSecret"
                    type="password"
                    class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Enter your Client Secret"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Access Token</label>
                <input
                  v-model="shopifyCredentials.accessToken"
                  type="password"
                  class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter your Shopify Access Token"
                />
              </div>
            </div>

            <!-- Save Button for Shopify Settings -->
            <div class="pt-4">
              <button 
                @click="saveSettings"
                :disabled="isSaving"
                :class="[
                  'w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark transition-colors duration-200',
                  saveSuccess ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500' : 'bg-primary hover:bg-primary-dark focus:ring-primary'
                ]"
              >
                <span class="flex items-center justify-center space-x-2">
                  <span>{{ isSaving ? 'Saving...' : 'Save Settings' }}</span>
                  <svg v-if="saveSuccess" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              </button>
              <div v-if="saveSuccess" class="mt-2 text-sm text-emerald-500">Settings saved!</div>
            </div>

            <!-- Connect with Shopify Button -->
            <div class="pt-4 border-t border-gray-700 mt-4">
               <h4 class="text-sm font-medium text-gray-300 mb-3">Or connect automatically</h4>
               <div class="relative group inline-block">
                 <button 
                  @click="connectToShopify"
                  :disabled="!shopifyCredentials.clientId || !shopifyCredentials.clientSecret"
                  :class="[
                    'w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark transition-colors duration-200 flex items-center justify-center space-x-2',
                    (!shopifyCredentials.clientId || !shopifyCredentials.clientSecret) ? 'bg-gray-600 cursor-not-allowed opacity-50' : 'bg-[#64943e] hover:bg-[#64943e]'
                  ]"
                >
                <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="30" height="30" viewBox="0 0 50 50"
                style="fill:#FFFFFF;"><path d="M 22.839844 2.0351562 C 19.659152 1.9484924 16.325547 6.1910156 14.529297 12.720703 L 8.8847656 14.560547 C 8.5177656 14.680547 8.2511719 14.999812 8.2011719 15.382812 L 4.7558594 42.193359 C 4.6888594 42.718359 5.0415 43.204734 5.5625 43.302734 L 29.630859 47.882812 L 31.488281 7.1972656 L 30.376953 7.5585938 C 29.713953 5.1145938 28.473844 3.5775156 26.839844 3.2285156 C 26.344844 3.1205156 25.866344 3.1427656 25.402344 3.2597656 C 24.845344 2.6717656 24.203656 2.266375 23.472656 2.109375 C 23.262469 2.0645 23.05189 2.0409338 22.839844 2.0351562 z M 22.720703 4.0371094 C 22.835859 4.0344238 22.948953 4.0437813 23.056641 4.0664062 C 23.227641 4.1034062 23.392734 4.17925 23.552734 4.28125 C 21.567734 5.97825 20.050047 9.1985469 19.248047 11.185547 L 16.847656 11.966797 C 18.482656 6.9417969 20.993363 4.0773926 22.720703 4.0371094 z M 26.65625 5.2675781 C 27.42425 5.5845781 28.071563 6.6167344 28.476562 8.1777344 L 27.453125 8.5117188 C 27.293125 7.2737188 27.02925 6.1775781 26.65625 5.2675781 z M 24.755859 5.9179688 C 25.120859 6.7669688 25.386484 7.865625 25.521484 9.140625 L 21.791016 10.355469 C 22.804016 8.2054687 23.847859 6.7299688 24.755859 5.9179688 z M 33.482422 7.3964844 L 31.636719 47.806641 L 44.216797 45.023438 C 44.728797 44.910438 45.065234 44.421297 44.990234 43.904297 C 43.389234 32.807297 40.349922 11.701203 40.294922 11.283203 C 40.283922 11.183203 40.25875 11.085188 40.21875 10.992188 C 40.02275 10.542188 39.668313 10.219641 39.195312 10.056641 C 39.103313 10.025641 38.993484 10.007906 38.896484 10.003906 C 38.674484 9.9949062 36.702281 9.8413906 35.863281 9.7753906 C 35.177281 9.0743906 33.860578 7.7328281 33.517578 7.4238281 C 33.506578 7.4128281 33.493422 7.4064844 33.482422 7.3964844 z M 22.71875 18.023438 C 24.78275 18.023438 25.880734 18.672172 25.927734 18.701172 C 26.124734 18.820172 26.213578 19.06125 26.142578 19.28125 L 24.630859 23.941406 C 24.584859 24.083406 24.479797 24.196953 24.341797 24.251953 C 24.201797 24.309953 24.046016 24.298516 23.916016 24.228516 C 23.903016 24.221516 22.63025 23.539063 21.15625 23.539062 C 19.37325 23.539062 19.107422 24.451141 19.107422 24.994141 C 19.107422 25.583141 19.880344 26.166844 20.777344 26.839844 C 22.364344 28.031844 24.537109 29.663453 24.537109 32.939453 C 24.537109 37.043453 21.982219 39.912109 18.324219 39.912109 C 14.164219 39.912109 12.069422 37.320937 11.982422 37.210938 C 11.881422 37.083937 11.848531 36.913813 11.894531 36.757812 L 12.957031 33.185547 C 13.006031 33.022547 13.136781 32.894656 13.300781 32.847656 C 13.463781 32.803656 13.641531 32.840172 13.769531 32.951172 C 13.789531 32.969172 15.726719 34.662109 17.261719 34.662109 C 18.154719 34.662109 18.390625 33.946359 18.390625 33.568359 C 18.390625 32.588359 17.673625 31.991328 16.765625 31.236328 C 15.511625 30.194328 13.953125 28.897031 13.953125 26.082031 C 13.953125 22.201031 16.658609 18.025391 22.599609 18.025391 C 22.639609 18.025391 22.67875 18.023438 22.71875 18.023438 z"></path>
                </svg>
                  <span>Connect to Shopify</span>
                </button>
                <div 
                  v-if="!shopifyCredentials.clientId || !shopifyCredentials.clientSecret"
                  class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none"
                >
                  Enter Client ID and Client Secret to connect
                  <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Allowed Domains -->
    <div class="bg-dark-secondary shadow rounded-lg p-4 sm:p-6">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <h3 class="text-lg sm:text-xl font-medium text-gray-100">Allowed Domains</h3>
          <p class="mt-1 text-sm text-gray-400">
            Configure which domains can use your search widget
          </p>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="space-y-4">
            <div v-for="(domain, index) in allowedDomains" :key="index" class="flex items-center space-x-2">
              <input 
                v-model="allowedDomains[index]"
                type="text"
                class="flex-1 bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="*.example.com"
              >
              <button 
                @click="removeDomain(index)"
                class="p-2 text-gray-400 hover:text-gray-300"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <button 
              @click="addDomain"
              class="text-sm text-primary hover:text-primary-dark flex items-center space-x-1"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Domain</span>
            </button>
          </div>

          <!-- Save Button for Allowed Domains -->
          <div class="pt-4">
            <button 
              @click="saveAllowedDomains"
              :disabled="isSavingDomains"
              :class="[
                'w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark transition-colors duration-200',
                saveDomainsSuccess ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500' : 'bg-primary hover:bg-primary-dark focus:ring-primary'
              ]"
            >
              <span class="flex items-center justify-center space-x-2">
                <span>{{ isSavingDomains ? 'Saving...' : 'Save Domains' }}</span>
                <svg v-if="saveDomainsSuccess" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
            </button>
            <div v-if="saveDomainsSuccess" class="mt-2 text-sm text-emerald-500">Domains saved!</div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Context -->
    <div class="bg-dark-secondary shadow rounded-lg p-4 sm:p-6">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <h3 class="text-lg sm:text-xl font-medium text-gray-100">AI Context</h3>
          <p class="mt-1 text-sm text-gray-400">
            Help the AI understand your business context
          </p>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Business Description</label>
              <textarea
                v-model="aiContext.businessDescription"
                rows="3"
                class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Describe your business, products, and target customers..."
              ></textarea>
            </div>

            <!-- Save Button for AI Context -->
            <div class="pt-4">
              <button 
                @click="saveAIContext"
                :disabled="isSavingAI"
                :class="[
                  'w-full sm:w-auto px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark transition-colors duration-200',
                  saveAISuccess ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500' : 'bg-primary hover:bg-primary-dark focus:ring-primary'
                ]"
              >
                <span class="flex items-center justify-center space-x-2">
                  <span>{{ isSavingAI ? 'Saving...' : 'Save AI Context' }}</span>
                  <svg v-if="saveAISuccess" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              </button>
              <div v-if="saveAISuccess" class="mt-2 text-sm text-emerald-500">AI Context saved!</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- API Keys -->
    <div class="bg-dark-secondary shadow rounded-lg p-4 sm:p-6">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <h3 class="text-lg sm:text-xl font-medium text-gray-100">API Keys</h3>
          <p class="mt-1 text-sm text-gray-400">
            Generate API keys to access Search Plus programmatically
          </p>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="space-y-4">
            <!-- Existing API Keys -->
            <div class="mb-8">
              <h4 class="text-base font-medium text-gray-100 mb-4">Existing API Keys</h4>
              
              <div v-if="isLoadingKeys" class="text-sm text-gray-400">
                Loading API keys...
              </div>
              
              <div v-else-if="loadKeysError" class="text-sm text-red-500">
                {{ loadKeysError }}
              </div>
              
              <div v-else-if="existingKeys.length === 0" class="text-sm text-gray-400">
                No API keys found. Generate one using the button below.
              </div>
              
              <div v-else class="space-y-3">
                <div v-for="key in existingKeys" :key="key.id" class="bg-dark-secondary border border-gray-700 rounded-md p-3">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <span class="font-mono text-gray-300">•••• {{ key.last_four_chars }}</span>
                      <span class="text-xs text-gray-500">{{ formatDate(key.created_at) }}</span>
                      <div class="flex gap-1">
                        <span v-for="scope in key.scopes" :key="scope" 
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-300">
                          {{ scope }}
                        </span>
                      </div>
                    </div>
                    <button 
                      @click="confirmDelete(key)"
                      :disabled="isDeletingKey === key.id"
                      class="text-gray-400 hover:text-red-400 transition-colors duration-200"
                    >
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Generated API Key Display -->
            <div v-if="generatedApiKey" class="relative mb-6">
              <label class="block text-sm font-medium text-gray-300 mb-2">Your New API Key</label>
              <p class="mb-1 text-sm text-yellow-400">
                Copy this key now. You won't be able to see it again!
              </p>
              <div class="relative">
                <input
                  type="text"
                  readonly
                  :value="generatedApiKey"
                  class="w-full bg-dark-secondary border border-gray-700 rounded-md text-gray-100 px-3 py-2 font-mono"
                />
                <button 
                  @click="copyApiKey"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 hover:text-gray-300 focus:outline-none"
                >
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" :stroke="copyApiKeySuccess ? '#10B981' : 'currentColor'">
                    <path v-if="copyApiKeySuccess" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Generate Key Button -->
            <div class="pt-4">
              <button 
                @click="generateApiKey"
                :disabled="isGeneratingKey"
                class="w-full sm:w-auto px-4 py-2 text-white bg-primary hover:bg-primary-dark rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark focus:ring-primary transition-colors duration-200"
              >
                <span class="flex items-center justify-center space-x-2">
                  <span>{{ isGeneratingKey ? 'Generating...' : 'Generate New API Key' }}</span>
                </span>
              </button>
              <div v-if="apiKeyError" class="mt-2 text-sm text-red-500">{{ apiKeyError }}</div>
            </div>

            <!-- Delete Confirmation Modal -->
            <BaseModal
              v-model="showDeleteModal"
            >
              <template #title>
                Delete API Key
              </template>
              <div class="space-y-4">
                <!-- Warning Header -->
                <div class="flex items-center text-yellow-400">
                  <svg class="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span class="font-medium">Warning: This action cannot be undone</span>
                </div>

                <!-- Key Details -->
                <div class="bg-dark-secondary border border-gray-700 rounded-md p-3">
                  <div class="flex items-center space-x-3">
                    <span class="font-mono text-gray-300">•••• {{ keyToDelete?.last_four_chars }}</span>
                    <span class="text-xs text-gray-500">{{ keyToDelete ? formatDate(keyToDelete.created_at) : '' }}</span>
                  </div>
                </div>

                <!-- Warning Details -->
                <div class="space-y-3 text-gray-300">
                  <p>Deleting this API key will:</p>
                  <ul class="list-disc list-inside space-y-2 ml-2">
                    <li>Immediately invalidate the key</li>
                    <li>Stop all API requests using this key</li>
                    <li>Require updating any applications or services using this key</li>
                    <li>Require generating a new key if access needs to be restored</li>
                  </ul>
                </div>
              </div>
              <template #footer>
                <div class="flex justify-end space-x-3">
                  <button
                    @click="showDeleteModal = false"
                    class="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    @click="deleteAPIKey(keyToDelete?.id)"
                    :disabled="isDeletingKey === keyToDelete?.id"
                    class="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark focus:ring-red-500 transition-colors duration-200"
                  >
                    {{ isDeletingKey === keyToDelete?.id ? 'Deleting...' : 'Delete Key' }}
                  </button>
                </div>
              </template>
            </BaseModal>
          </div>
        </div>
      </div>
    </div>

    <!-- Installation Instructions -->
    <div class="bg-dark-secondary shadow rounded-lg p-4 sm:p-6">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <h3 class="text-lg sm:text-xl font-medium text-gray-100">Installation</h3>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="space-y-4">
            <div class="relative">
              <label class="block text-sm font-medium text-gray-300 mb-2">Header Tracking Script</label>
              <p class="mb-1 text-sm text-gray-400">Add this code to the header of your website to enable the search</p>
              <div class="relative overflow-x-auto">
                <pre class="bg-dark-secondary border border-gray-700 rounded-md text-gray-100 p-4 font-mono text-sm whitespace-pre-wrap break-all"><code><span class="text-orange-400">&lt;script</span> <span class="text-sky-300">src</span>=<span class="text-emerald-300">"https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/script"</span><span class="text-orange-400">&gt;&lt;/script&gt;</span>
<span class="text-orange-400">&lt;script&gt;</span><span class="text-gray-100">window</span><span class="text-gray-400">.</span><span class="text-gray-100">sp</span> <span class="text-gray-400">=</span> <span class="text-gray-100">window</span><span class="text-gray-400">.</span><span class="text-gray-100">sp</span> <span class="text-gray-400">||</span> <span class="text-purple-400">new</span> <span class="text-yellow-300">SearchPlus</span>({ <span class="text-sky-300">org_id:</span> <span class="text-emerald-300">'{{ orgId }}'</span> });<span class="text-orange-400">&lt;/script&gt;</span></code></pre>
              </div>
              <button 
                @click="copyInstallCode"
                class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-300 focus:outline-none"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" :stroke="copySuccess ? '#10B981' : 'currentColor'">
                  <path v-if="copySuccess" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            <div class="relative">
              <label class="block text-sm font-medium text-gray-300 mb-2">Conversion Tracking Script</label>
              <p class="mb-1 text-sm text-gray-400">Add this code to the customer events in shopify as a custom pixel</p>
              <div class="relative overflow-x-auto">
                <pre class="bg-dark-secondary border border-gray-700 rounded-md text-gray-100 p-4 font-mono text-sm whitespace-pre-wrap break-all"><code><span class="text-gray-100">analytics</span><span class="text-gray-400">.</span><span class="text-yellow-300">subscribe</span>(<span class="text-emerald-300">"checkout_completed"</span>, <span class="text-purple-400">function</span> (<span class="text-orange-300">event</span>) {
  <span class="text-purple-400">const</span> <span class="text-sky-300">search_id</span> <span class="text-gray-400">=</span> <span class="text-gray-100">event</span><span class="text-gray-400">.</span><span class="text-gray-100">data</span><span class="text-gray-400">.</span><span class="text-gray-100">checkout</span><span class="text-gray-400">.</span><span class="text-gray-100">attributes</span><span class="text-gray-400">.</span><span class="text-gray-100">search_plus_id</span>;
  <span class="text-purple-400">if</span>(<span class="text-gray-400">!</span><span class="text-gray-100">search_id</span>) <span class="text-purple-400">return</span>;
  <span class="text-purple-400">const</span> <span class="text-sky-300">script</span> <span class="text-gray-400">=</span> <span class="text-gray-100">document</span><span class="text-gray-400">.</span><span class="text-yellow-300">createElement</span>(<span class="text-emerald-300">'script'</span>);
  <span class="text-gray-100">script</span><span class="text-gray-400">.</span><span class="text-yellow-300">setAttribute</span>(<span class="text-emerald-300">'src'</span>, <span class="text-emerald-300">`https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/conversion?amount=${</span><span class="text-gray-100">event</span><span class="text-gray-400">.</span><span class="text-gray-100">data</span><span class="text-gray-400">.</span><span class="text-gray-100">checkout</span><span class="text-gray-400">.</span><span class="text-gray-100">totalPrice</span><span class="text-gray-400">?.</span><span class="text-gray-100">amount</span><span class="text-emerald-300">}&search_id=${</span><span class="text-gray-100">search_id</span><span class="text-emerald-300">}&order_id=${</span><span class="text-gray-100">event</span><span class="text-gray-400">.</span><span class="text-gray-100">data</span><span class="text-gray-400">.</span><span class="text-gray-100">checkout</span><span class="text-gray-400">.</span><span class="text-gray-100">order</span><span class="text-gray-400">?.</span><span class="text-gray-100">id</span><span class="text-emerald-300">}`</span>);
  <span class="text-gray-100">script</span><span class="text-gray-400">.</span><span class="text-yellow-300">setAttribute</span>(<span class="text-emerald-300">'async'</span>, <span class="text-emerald-300">''</span>);
  <span class="text-gray-100">document</span><span class="text-gray-400">.</span><span class="text-gray-100">head</span><span class="text-gray-400">.</span><span class="text-yellow-300">appendChild</span>(<span class="text-gray-100">script</span>);
});</code></pre>
              </div>
              <button 
                @click="copyConversionCode"
                class="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-300 focus:outline-none"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" :stroke="copyConversionSuccess ? '#10B981' : 'currentColor'">
                  <path v-if="copyConversionSuccess" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { db, auth } from '@/config/firebase'
import { doc, getDoc, updateDoc, onSnapshot, deleteDoc } from 'firebase/firestore'
import { useAuthStore } from '@/stores/auth'
import { functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'
import BaseModal from '@/components/ui/BaseModal.vue'

interface AIContext {
  businessDescription: string
}

interface OrgConfig {
  id: string
  created_at: Date
  updated_at: Date
  keys: {
    shopify?: {
      storeName: string
      apiKey: string
      secretKey: string
      accessToken: string
      clientId?: string
      clientSecret?: string
      isConnected: boolean
      lastSyncTime: string | null
    }
  }
  allowed_origins: string[]
  analysis_categories: string[]
  daily_insights: any[]
  prompt: string
  aiContext?: AIContext
}

const authStore = useAuthStore()
const route = useRoute()
const isConnected = ref(false)
const isRefreshing = ref(false)
const isSaving = ref(false)
const copySuccess = ref(false)
const copyConversionSuccess = ref(false)
const orgId = ref('')
const isSavingDomains = ref(false)
const isSavingAI = ref(false)
const lastSyncTime = ref<string | null>(null)
const allowedDomains = ref<string[]>([''])
const aiContext = ref<AIContext>({
  businessDescription: ''
})

const shopifyCredentials = ref({
  storeName: '',
  apiKey: '',
  secretKey: '',
  accessToken: '',
  clientId: '',
  clientSecret: ''
})

const saveSuccess = ref(false)
const saveDomainsSuccess = ref(false)
const saveAISuccess = ref(false)

let unsubscribeOrg: (() => void) | null = null

const copyInstallCode = async () => {
  try {
    const installCode = `<script src="https://api.searchplus.io/script.js"><\/script>
<script>
window.sp = new SearchPlus({ org_id: '${orgId.value}' });
<\/script>`
    await navigator.clipboard.writeText(installCode)
    copySuccess.value = true
    setTimeout(() => {
      copySuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error copying to clipboard:', error)
  }
}

const copyConversionCode = async () => {
  try {
    const conversionCode = `analytics.subscribe("checkout_completed", function (event) {
  const search_id = event.data.checkout.attributes.search_plus_id;
  if(!search_id) return;
  const script = document.createElement('script');
  script.setAttribute('src', \`https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/conversion?amount=\${event.data.checkout.totalPrice?.amount}&search_id=\${search_id}&order_id=\${event.data.checkout.order?.id}\`);
  script.setAttribute('async', '');
  document.head.appendChild(script);
});`
    await navigator.clipboard.writeText(conversionCode)
    copyConversionSuccess.value = true
    setTimeout(() => {
      copyConversionSuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error copying conversion code to clipboard:', error)
  }
}

const loadSettings = async () => {
  try {
    // First get the user's document to get their org_id
    if (!auth.currentUser) return
    
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
    if (!userDoc.exists()) {
      console.error('User document not found')
      return
    }

    const userData = userDoc.data()
    orgId.value = userData.org_id
    if (!orgId.value) {
      console.error('No org_id found for user')
      return
    }

    // Set up real-time listener for the org document
    if (unsubscribeOrg) {
      unsubscribeOrg()
    }

    unsubscribeOrg = onSnapshot(doc(db, 'config', orgId.value), (orgDoc) => {
      if (!orgDoc.exists()) {
        console.error('Org document not found')
        return
      }

      const orgData = orgDoc.data() as OrgConfig
      
      // Set the component data from org config
      allowedDomains.value = orgData.allowed_origins || ['']
      aiContext.value = orgData.aiContext || {
          businessDescription: ''
        }

      // Set Shopify credentials if they exist
      if (orgData.keys?.shopify) {
        const shopifyKeys = orgData.keys.shopify
        shopifyCredentials.value = {
          storeName: shopifyKeys.storeName || '',
          apiKey: shopifyKeys.apiKey || '',
          secretKey: shopifyKeys.secretKey || '',
          accessToken: shopifyKeys.accessToken || '',
          clientId: shopifyKeys.clientId || '',
          clientSecret: shopifyKeys.clientSecret || ''
        }
        isConnected.value = shopifyKeys.isConnected || false
        lastSyncTime.value = shopifyKeys.lastSyncTime || null
      } else {
        // Reset values if Shopify keys don't exist
        shopifyCredentials.value = {
          storeName: '',
          apiKey: '',
          secretKey: '',
          accessToken: '',
          clientId: '',
          clientSecret: ''
        }
        isConnected.value = false
        lastSyncTime.value = null
      }
    }, (error) => {
      console.error('Error listening to org document:', error)
    })

  } catch (error) {
    console.error('Error loading settings:', error)
  }
}

const connectToShopify = () => {
  if (!shopifyCredentials.value.storeName) {
    alert('Please enter a store name')
    return
  }
  
  const storeName = shopifyCredentials.value.storeName.replace('.myshopify.com', '').replace('https://', '').replace('/', '')
  const functionUrl = `https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/shopifyAuth`
  window.location.href = `${functionUrl}?shop=${storeName}.myshopify.com&org_id=${orgId.value}`
}

const saveSettings = async () => {
  if (!auth.currentUser) return
  isSaving.value = true
  saveSuccess.value = false
  
  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
    if (!userDoc.exists()) return
    
    const orgIdValue = userDoc.data().org_id
    if (!orgIdValue) return

    await updateDoc(doc(db, 'config', orgIdValue), {
      'keys.shopify': {
        ...shopifyCredentials.value,
      isConnected: isConnected.value,
        lastSyncTime: lastSyncTime.value
      },
      updated_at: new Date()
    })
    
    // Show success state
    saveSuccess.value = true
    // Reset success state after 2 seconds
    setTimeout(() => {
      saveSuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error saving settings:', error)
  } finally {
    isSaving.value = false
  }
}

const saveAllowedDomains = async () => {
  if (!auth.currentUser) return
  isSavingDomains.value = true
  saveDomainsSuccess.value = false
  
  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
    if (!userDoc.exists()) return
    
    const orgIdValue = userDoc.data().org_id
    if (!orgIdValue) return

    await updateDoc(doc(db, 'config', orgIdValue), {
      allowed_origins: allowedDomains.value.filter(Boolean),
      updated_at: new Date()
    })
    
    // Show success state
    saveDomainsSuccess.value = true
    // Reset success state after 2 seconds
    setTimeout(() => {
      saveDomainsSuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error saving allowed domains:', error)
  } finally {
    isSavingDomains.value = false
  }
}

const saveAIContext = async () => {
  if (!auth.currentUser) return
  isSavingAI.value = true
  saveAISuccess.value = false
  
  try {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
    if (!userDoc.exists()) return
    
    const orgIdValue = userDoc.data().org_id
    if (!orgIdValue) return

    await updateDoc(doc(db, 'config', orgIdValue), {
      aiContext: aiContext.value,
      updated_at: new Date()
    })
    
    // Show success state
    saveAISuccess.value = true
    // Reset success state after 2 seconds
    setTimeout(() => {
      saveAISuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error saving AI context:', error)
  } finally {
    isSavingAI.value = false
  }
}

const addDomain = () => {
  allowedDomains.value.push('')
}

const removeDomain = (index: number) => {
  allowedDomains.value.splice(index, 1)
}

const formatLastSync = (date: string | null) => {
  if (!date) return 'Never'
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  
  if (diff < 60000) return 'Just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`
  return `${Math.floor(diff / 86400000)} days ago`
}

const formatDate = (date: Date | any) => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return 'Invalid Date'

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  }).format(d)
}

const disconnectShopify = async () => {
  // Implement disconnect logic
  isConnected.value = false
  shopifyCredentials.value.accessToken = '' // Clear access token on disconnect
}

// API Key Management
interface APIKey {
  id: string
  last_four_chars: string
  created_at: Date
  scopes: string[]
}

const generatedApiKey = ref<string>('')
const isGeneratingKey = ref(false)
const apiKeyError = ref<string>('')
const copyApiKeySuccess = ref(false)
const existingKeys = ref<APIKey[]>([])
const isLoadingKeys = ref(false)
const loadKeysError = ref<string>('')
const isDeletingKey = ref<string | null>(null)
const showDeleteModal = ref(false)
const keyToDelete = ref<APIKey | null>(null)

const confirmDelete = (key: APIKey) => {
  keyToDelete.value = key
  showDeleteModal.value = true
}

const loadAPIKeys = async () => {
  if (!auth.currentUser) return
  isLoadingKeys.value = true
  loadKeysError.value = ''
  
  try {
    const getAPIKeys = httpsCallable(functions, 'getAPIKeys')
    const result = await getAPIKeys()
    const { success, api_keys } = (result.data as any)
    
    if (success && api_keys) {
      existingKeys.value = api_keys.map((key: any) => {
        let date = key.created_at
        // Handle Firestore timestamp objects
        if (date && typeof date === 'object' && (date._seconds || date.seconds)) {
          date = new Date((date._seconds || date.seconds) * 1000)
        } else {
          date = new Date(date)
        }
        return {
          ...key,
          created_at: date
        }
      })
    } else {
      throw new Error('Failed to load API keys')
    }
  } catch (error) {
    console.error('Error loading API keys:', error)
    loadKeysError.value = 'Failed to load API keys. Please try again.'
  } finally {
    isLoadingKeys.value = false
  }
}

const generateApiKey = async () => {
  if (!auth.currentUser) return
  isGeneratingKey.value = true
  apiKeyError.value = ''
  
  try {
    const createAPIKey = httpsCallable(functions, 'createAPIKey')
    const result = await createAPIKey()
    const { success, api_key } = result.data as { success: boolean; api_key: string; uid: string }
    
    if (success && api_key) {
      generatedApiKey.value = api_key
      // Reload the list of API keys
      await loadAPIKeys()
    } else {
      throw new Error('Failed to generate API key')
    }
  } catch (error) {
    console.error('Error generating API key:', error)
    apiKeyError.value = 'Failed to generate API key. Please try again.'
  } finally {
    isGeneratingKey.value = false
  }
}

const copyApiKey = async () => {
  try {
    await navigator.clipboard.writeText(generatedApiKey.value)
    copyApiKeySuccess.value = true
    setTimeout(() => {
      copyApiKeySuccess.value = false
    }, 2000)
  } catch (error) {
    console.error('Error copying API key to clipboard:', error)
    apiKeyError.value = 'Failed to copy API key. Please try copying manually.'
  }
}

const deleteAPIKey = async (keyId: string | undefined) => {
  if (!auth.currentUser || !keyId) return
  isDeletingKey.value = keyId
  
  try {
    await deleteDoc(doc(db, 'api-keys', keyId))
    // Remove the key from the local list
    existingKeys.value = existingKeys.value.filter(key => key.id !== keyId)
    // Close the modal
    showDeleteModal.value = false
    keyToDelete.value = null
  } catch (error) {
    console.error('Error deleting API key:', error)
    loadKeysError.value = 'Failed to delete API key. Please try again.'
  } finally {
    isDeletingKey.value = null
  }
}

onMounted(async () => {
  // Initialize auth store if not already done
  await authStore.init()
  
  // Wait for auth state to be loaded
  if (!authStore.loading && auth.currentUser) {
    await loadSettings()
    await loadAPIKeys()

    if (route.query.success === 'true') {
      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 5000)
    }
  }
})

// Add watcher for auth loading state
watch(
  () => authStore.loading,
  async (newLoading) => {
    if (!newLoading && auth.currentUser) {
      await loadSettings()
      await loadAPIKeys()
    }
  }
)

// Cleanup listener when component is unmounted
onUnmounted(() => {
  if (unsubscribeOrg) {
    unsubscribeOrg()
  }
})
</script> 